import { StandardProps } from "@/types/types";
import { FoundItLink } from "@/types/foundIt";
import { useRouter } from "next/router";
import classNames from "classnames";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import DEPRECATED_Carousel from "@JohnLewisPartnership/jl-design-system/dist/components/carousel/DEPRECATED_Carousel";
import { useMemo } from "react";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import { useRouteParams } from "@/hooks/useRouteParams";
import styles from "./FoundIt.module.scss";
import { FOUNDIT_CLICK } from "@/utils/events";
import { useFeatureFlags } from "@/flags/flags";
import { validateQueryParams } from "./utils";
import { sanitiseQueryParams } from "@/utils/queryUtils.ts";

type FoundItProps = Pick<StandardProps, "configProps" | "foundItData">;

export function FoundIt({ foundItData, configProps }: FoundItProps) {
  const router = useRouter();
  const { isFoundItFlagOn } = useFeatureFlags();

  const { pageType, queryWithoutPageOrChunk } = useRouteParams();

  const imageLimit = (configProps?.imageLimit as number) || 12;

  const maxSlidesToDisplay = (configProps?.maxSlides as number) || 4;

  const greyBackground = configProps?.background === "grey";

  const fullWidth = configProps?.fullWidth;

  const links: FoundItLink[] = useMemo(() => {
    return foundItData.slice(0, imageLimit) || [];
  }, [foundItData, imageLimit]);

  if (!isFoundItFlagOn.value || !foundItData || foundItData.length < 4) {
    return null;
  }

  const navigateToDestinationUrl = (link: FoundItLink) => {
    // append any existing query params from the DestinationUrl
    const targetUrl = new URL(link.DestinationUrl);
    const targetSearchParams = new URLSearchParams(targetUrl.search);

    const queryParams = validateQueryParams({
      ...Object.fromEntries(targetSearchParams),
      ...queryWithoutPageOrChunk,
      // append fi=c parameter for FoundIt analytics
      fi: "c",
    });

    const url = {
      pathname: `${targetUrl.pathname}`,
      query: queryParams,
    };

    const as = `${targetUrl.pathname}?${sanitiseQueryParams(queryParams, { retainFoundItTracking: true })}`;

    router.push(url, as);
  };

  const onFoundItClick = (
    evt: React.MouseEvent<Element>,
    link: FoundItLink,
  ) => {
    evt.preventDefault();
    const { Order, AnchorText, DestinationUrl } = link;
    dispatchLegacyAnalyticsEvent(FOUNDIT_CLICK, {
      foundItParam: `${router.asPath}|${pageType}|main-links|${Order}|${links.length}|${AnchorText}|${DestinationUrl}`,
      foundItLink: link.AnchorText,
    });
    navigateToDestinationUrl(link);
  };

  return (
    <div data-component="FoundIt">
      <DEPRECATED_Carousel
        className={classNames(styles.links, {
          [styles["links--fullWidth"]!]: fullWidth,
        })}
        data-test="found-it-carousel"
        dots={false}
        largeChevrons
        slidesToShow={maxSlidesToDisplay}
        slidesToScroll={maxSlidesToDisplay}
      >
        {links.map((link, i) => {
          const { AnchorText, DestinationUrl, ImageUrl } = link;
          return (
            <div
              key={i}
              className={classNames(styles.link, {
                [styles.link__first!]: i === 0,
                [styles.link__last!]: i === links.length - 1,
                [styles.link__middle!]:
                  fullWidth && i == 5 && links.length == 12,
              })}
            >
              <UnderlinedButton
                className={classNames(styles.externalButton, {
                  [styles.grey!]: greyBackground,
                })}
                tag="a"
                rel="noreferrer"
                href={DestinationUrl}
                onClick={(evt: React.MouseEvent<Element>) =>
                  onFoundItClick(evt, link)
                }
              >
                {ImageUrl && (
                  <img src={ImageUrl} alt={`Click to view ${AnchorText}`} />
                )}
                <span>{AnchorText}</span>
              </UnderlinedButton>
            </div>
          );
        })}
      </DEPRECATED_Carousel>
    </div>
  );
}
